/**
 * useInoculation Hook
 *
 * Provides CRUD operations and data fetching for inoculation events via Edge Function.
 * Part of US-P2-005: Inoculation & Production Batch Creation
 *
 * Inoculation events allocate spawn to substrate batches, creating production batches.
 * Each event generates a unique production batch code (YYMMDDXX format) tied to a strain.
 */

import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/supabase-client'

/**
 * Call inoculation-events Edge Function
 * @param {string} action - The action to perform
 * @param {Object} data - Request payload
 * @returns {Promise<Object>} Response data
 */
async function callInoculationFunction(action, data = {}) {
  const { data: responseData, error } = await supabase.functions.invoke('inoculation-events', {
    body: { action, ...data }
  })

  // Log full response for debugging
  console.log('[callInoculationFunction] Response:', { responseData, error })

  if (error) {
    // Try to extract detailed error message from FunctionsHttpError
    let detailedMessage = error.message || 'Edge function error'

    // FunctionsHttpError has a context property with the response
    if (error.context) {
      try {
        const errorBody = await error.context.json()
        console.error('[callInoculationFunction] Error body:', errorBody)
        detailedMessage = errorBody.message || errorBody.error || detailedMessage
      } catch (e) {
        console.error('[callInoculationFunction] Could not parse error body:', e)
      }
    }

    console.error('[callInoculationFunction] Error details:', { error, responseData, detailedMessage })
    throw new Error(detailedMessage)
  }

  if (responseData?.error) {
    throw new Error(responseData.message || responseData.error)
  }

  return responseData
}

/**
 * Hook to fetch all inoculation events with optional filtering
 * @param {Object} options - Filter options
 * @param {string} options.strainId - Filter by strain
 * @param {string} options.dateFrom - Filter by event_date from
 * @param {string} options.dateTo - Filter by event_date to
 * @param {number} options.limit - Limit results
 * @param {number} options.offset - Pagination offset
 * @returns {Object} { events, loading, error, refetch }
 */
export function useInoculationEvents(options = {}) {
  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchEvents = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      const response = await callInoculationFunction('list', {
        strain_id: options.strainId,
        date_from: options.dateFrom,
        date_to: options.dateTo,
        limit: options.limit || 50,
        offset: options.offset || 0
      })

      setEvents(response.data || [])
    } catch (err) {
      console.error('[useInoculationEvents] Error:', err)
      setError(err.message)
      setEvents([])
    } finally {
      setLoading(false)
    }
  }, [options.strainId, options.dateFrom, options.dateTo, options.limit, options.offset])

  useEffect(() => {
    fetchEvents()
  }, [fetchEvents])

  return {
    events,
    loading,
    error,
    refetch: fetchEvents
  }
}

/**
 * Hook to fetch a single inoculation event with full details
 * @param {string} eventId - Inoculation event UUID
 * @returns {Object} { event, loading, error, refetch }
 */
export function useInoculationEvent(eventId) {
  const [event, setEvent] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchEvent = useCallback(async () => {
    if (!eventId) {
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const response = await callInoculationFunction('get', {
        event_id: eventId
      })

      setEvent(response.data)
    } catch (err) {
      console.error('[useInoculationEvent] Error:', err)
      setError(err.message)
      setEvent(null)
    } finally {
      setLoading(false)
    }
  }, [eventId])

  useEffect(() => {
    fetchEvent()
  }, [fetchEvent])

  return {
    event,
    loading,
    error,
    refetch: fetchEvent
  }
}

/**
 * Hook to fetch substrate batches ready for inoculation
 * @returns {Object} { substrates, loading, error, refetch }
 */
export function useReadySubstrates() {
  const [substrates, setSubstrates] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchSubstrates = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      const response = await callInoculationFunction('ready_substrates')
      setSubstrates(response.data || [])
    } catch (err) {
      console.error('[useReadySubstrates] Error:', err)
      setError(err.message)
      setSubstrates([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchSubstrates()
  }, [fetchSubstrates])

  return {
    substrates,
    loading,
    error,
    refetch: fetchSubstrates
  }
}

/**
 * Hook to fetch spawn batches available for inoculation
 * @param {string} strainId - Optional strain filter
 * @returns {Object} { spawnBatches, loading, error, refetch }
 */
export function useAvailableSpawn(strainId = null) {
  const [spawnBatches, setSpawnBatches] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchSpawnBatches = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      const response = await callInoculationFunction('available_spawn', {
        strain_id: strainId
      })

      setSpawnBatches(response.data || [])
    } catch (err) {
      console.error('[useAvailableSpawn] Error:', err)
      setError(err.message)
      setSpawnBatches([])
    } finally {
      setLoading(false)
    }
  }, [strainId])

  useEffect(() => {
    fetchSpawnBatches()
  }, [fetchSpawnBatches])

  return {
    spawnBatches,
    loading,
    error,
    refetch: fetchSpawnBatches
  }
}

/**
 * Hook to fetch spawn batches available for inoculation WITH individual bag data
 * Used for bag-level selection during inoculation events
 * @param {string} strainId - Optional strain filter
 * @returns {Object} { spawnBatches, loading, error, refetch }
 */
export function useAvailableSpawnWithBags(strainId = null) {
  const [spawnBatches, setSpawnBatches] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchSpawnBatches = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      const response = await callInoculationFunction('available_spawn_with_bags', {
        strain_id: strainId
      })

      setSpawnBatches(response.data || [])
    } catch (err) {
      console.error('[useAvailableSpawnWithBags] Error:', err)
      setError(err.message)
      setSpawnBatches([])
    } finally {
      setLoading(false)
    }
  }, [strainId])

  useEffect(() => {
    fetchSpawnBatches()
  }, [fetchSpawnBatches])

  return {
    spawnBatches,
    loading,
    error,
    refetch: fetchSpawnBatches
  }
}

/**
 * Hook to fetch inoculation statistics
 * @returns {Object} { stats, loading, error, refetch }
 */
export function useInoculationStats() {
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchStats = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      const response = await callInoculationFunction('stats')
      setStats(response.data)
    } catch (err) {
      console.error('[useInoculationStats] Error:', err)
      setError(err.message)
      setStats(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchStats()
  }, [fetchStats])

  return {
    stats,
    loading,
    error,
    refetch: fetchStats
  }
}

/**
 * Hook for inoculation mutations (create, calculate spawn)
 * @returns {Object} Mutation functions and state
 */
export function useInoculationMutations() {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  /**
   * Create a new inoculation event
   * @param {Object} data - Inoculation event data
   * @param {string} data.strain_id - Strain UUID
   * @param {string} data.event_date - Inoculation date (YYYY-MM-DD)
   * @param {number} data.spawn_rate_percent - Spawn rate as percentage (e.g., 3.5)
   * @param {Array} data.allocations - Array of {substrate_batch_id, blocks_allocated}
   * @param {Array} data.spawn_usage - Array of {spawn_batch_id, quantity_grams}
   * @param {string} data.notes - Optional notes
   * @returns {Promise<Object>} Created event with production batch
   */
  const createInoculation = useCallback(async (data) => {
    setLoading(true)
    setError(null)

    try {
      const response = await callInoculationFunction('create', data)
      return response.data
    } catch (err) {
      console.error('[useInoculationMutations.createInoculation] Error:', err)
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  /**
   * Calculate spawn needed for inoculation
   * @param {Object} params - Calculation parameters
   * @param {number} params.blocks - Number of blocks
   * @param {number} params.block_weight_kg - Weight per block in kg
   * @param {number} params.spawn_rate_percent - Spawn rate as percentage
   * @returns {Promise<Object>} { spawn_grams, spawn_kg }
   */
  const calculateSpawn = useCallback(async (params) => {
    setLoading(true)
    setError(null)

    try {
      const response = await callInoculationFunction('calculate_spawn', params)
      return response.data
    } catch (err) {
      console.error('[useInoculationMutations.calculateSpawn] Error:', err)
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  return {
    loading,
    error,
    createInoculation,
    calculateSpawn
  }
}

/**
 * Helper function to calculate spawn needed (client-side)
 * @param {number} blocks - Number of blocks
 * @param {number} blockWeightKg - Weight per block in kg
 * @param {number} spawnRatePercent - Spawn rate as percentage (e.g., 3.5)
 * @returns {Object} { spawn_grams, spawn_kg }
 */
export function calculateSpawnNeeded(blocks, blockWeightKg, spawnRatePercent) {
  if (!blocks || !blockWeightKg || !spawnRatePercent) {
    return { spawn_grams: 0, spawn_kg: 0 }
  }

  const totalSubstrateKg = blocks * blockWeightKg
  const spawnKg = totalSubstrateKg * (spawnRatePercent / 100)
  const spawnGrams = spawnKg * 1000

  return {
    spawn_grams: Math.round(spawnGrams),
    spawn_kg: parseFloat(spawnKg.toFixed(3))
  }
}

/**
 * Helper function to format production batch code
 * @param {Date|string} date - Event date
 * @param {string} strainCode - Strain code (e.g., "OY", "PE")
 * @returns {string} Production batch code (e.g., "250115OY")
 */
export function formatProductionBatchCode(date, strainCode) {
  const d = typeof date === 'string' ? new Date(date) : date

  const year = d.getFullYear().toString().slice(-2)
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')

  return `${year}${month}${day}${strainCode}`
}

export default useInoculationEvents
